import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, Filter, X, Baby } from "lucide-react";
import ListingCard from "@/components/ListingCard";
import { browseApi } from "@/api/browse";
import { useAuth } from "@/App";
import { logger } from "@/utils/logger";

const SPECIES_TABS = [
  { value: "", label: "All", icon: "🐾" },
  { value: "canine", label: "Puppies", icon: "🐕" },
  { value: "feline", label: "Kittens", icon: "🐱" },
  { value: "equine", label: "Foals", icon: "🐴" },
  { value: "exotics", label: "Exotics", icon: "🦜" },
  { value: "others", label: "Farm Animals", icon: "🐖" },
];

const Litters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Get active tab from URL or default to "all"
  const activeSpecies = searchParams.get("species") || "";
  const [filters, setFilters] = useState({
    breed: searchParams.get("breed") || "",
    state: searchParams.get("state") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
  });

  useEffect(() => {
    fetchLitters();
  }, [activeSpecies, searchParams]);

  const fetchLitters = async () => {
    setLoading(true);
    try {
      const params = {
        listing_type: "offspring",
        limit: 24,
      };
      
      if (activeSpecies) {
        params.species = activeSpecies;
      }
      
      // Add filters
      if (filters.breed) params.breed = filters.breed;
      if (filters.state) params.state = filters.state;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;

      const data = await browseApi.search(params);
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (err) {
      logger.error("Failed to fetch litters:", err);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (species) => {
    const newParams = new URLSearchParams(searchParams);
    if (species) {
      newParams.set("species", species);
    } else {
      newParams.delete("species");
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams();
    if (activeSpecies) newParams.set("species", activeSpecies);
    if (filters.breed) newParams.set("breed", filters.breed);
    if (filters.state) newParams.set("state", filters.state);
    if (filters.min_price) newParams.set("min_price", filters.min_price);
    if (filters.max_price) newParams.set("max_price", filters.max_price);
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ breed: "", state: "", min_price: "", max_price: "" });
    const newParams = new URLSearchParams();
    if (activeSpecies) newParams.set("species", activeSpecies);
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const getActiveLabel = () => {
    if (!activeSpecies) return "Offspring";
    const tab = SPECIES_TABS.find(t => t.value === activeSpecies);
    return tab ? tab.label : "Offspring";
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="px-6 md:px-12 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Baby className="w-8 h-8 text-[#D4AF37]" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">Available Litters</p>
          </div>
          <h1 className="text-4xl md:text-5xl text-[#EDEDED] mb-4">
            {activeSpecies ? getActiveLabel() : "Available Offspring"}
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-2xl">
            Browse available puppies, kittens, foals, and exotic offspring from verified breeders nationwide.
          </p>
        </div>
      </section>

      {/* Species Tabs */}
      <section className="px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 p-1 bg-[#18181B] rounded-xl w-fit">
            {SPECIES_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeSpecies === tab.value
                    ? "bg-[#D4AF37] text-black"
                    : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#27272A]"
                }`}
                data-testid={`tab-${tab.value || 'all'}`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-[#71717A] text-sm">
            {loading ? "Loading..." : `${total} ${total === 1 ? "listing" : "listings"} found`}
          </p>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#3F3F46] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#EDEDED] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </section>

      {/* Filter Panel */}
      {showFilters && (
        <section className="px-6 md:px-12 mb-8">
          <div className="max-w-7xl mx-auto">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg text-[#EDEDED]">Filter Results</h3>
                <button onClick={() => setShowFilters(false)} className="text-[#71717A] hover:text-[#EDEDED]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Breed</label>
                  <input
                    type="text"
                    value={filters.breed}
                    onChange={(e) => handleFilterChange("breed", e.target.value)}
                    placeholder="e.g., Golden Retriever"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">State</label>
                  <input
                    type="text"
                    value={filters.state}
                    onChange={(e) => handleFilterChange("state", e.target.value)}
                    placeholder="e.g., TX"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Min Price</label>
                  <input
                    type="number"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange("min_price", e.target.value)}
                    placeholder="$0"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Max Price</label>
                  <input
                    type="number"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange("max_price", e.target.value)}
                    placeholder="$10,000"
                    className="input-minimal"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={applyFilters}
                  className="px-6 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="px-6 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Listings Grid */}
      <section className="px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#18181B] flex items-center justify-center">
                <Baby className="w-10 h-10 text-[#3F3F46]" />
              </div>
              <h3 className="text-xl text-[#EDEDED] mb-2">No {getActiveLabel()} Available</h3>
              <p className="text-[#71717A] mb-8 max-w-md mx-auto">
                {activeSpecies 
                  ? `There are currently no ${getActiveLabel().toLowerCase()} listed. Check back soon or browse other categories.`
                  : "There are currently no offspring listed. Check back soon or become a breeder to list your first litter."
                }
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => {
                    if (!user) {
                      navigate('/signin?returnUrl=/subscription-plans');
                    } else if (!user.roles?.includes('breeder')) {
                      navigate('/subscription-plans');
                    } else {
                      navigate('/dashboard/breeder/new-listing');
                    }
                  }}
                  className="px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#C4A030] transition-colors"
                >
                  Start Listing
                </button>
                <Link
                  to="/"
                  className="px-6 py-3 border border-[#3F3F46] text-[#EDEDED] rounded-lg hover:border-[#D4AF37] transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Litters;
